// oxlint-disable-next-line @typescript-eslint/consistent-type-imports
import React, { useState, useCallback } from 'react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPen } from '@fortawesome/pro-solid-svg-icons/faPen';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { messages } from './messages';
import style from './style.module.less';
import { useBinaryState } from '../../../../hooks/use-binary-state';
import { refreshLabelProviders, refreshSellerWallets } from '../../../../store/seller/seller.actions';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { useWallet } from '../../../../wallet/hooks/use-wallet';
import { updateWalletNickname } from '../../../../wallet/utilities/api';

const MAX_NICKNAME_LENGTH = 50;

type WalletNicknameEditorProps = {
  walletId: string;
};

export const WalletNicknameEditor = ({ walletId }: WalletNicknameEditorProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const wallet = useWallet(walletId);

  const [isEditing, startEditing, stopEditing] = useBinaryState(false);
  const [editedNickname, setEditedNickname] = useState('');
  const [error, setError] = useAsyncSafeState(null);

  const handleEditClick = useCallback(() => {
    setEditedNickname(wallet.nickname ?? '');
    setError(null);
    startEditing();
  }, [wallet.nickname, startEditing, setError]);

  const handleNicknameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value.length <= MAX_NICKNAME_LENGTH) {
        setEditedNickname(value);
        setError(null);
      }
    },
    [setError],
  );

  const handleSave = useCallback(async () => {
    const trimmedNickname = editedNickname.trim();
    if (!trimmedNickname) {
      return;
    }

    const isSaveDisabled = !trimmedNickname || trimmedNickname === wallet.nickname;

    try {
      if (!isSaveDisabled) {
        await updateWalletNickname(walletId, trimmedNickname);
        dispatch(refreshSellerWallets());
        dispatch(refreshLabelProviders({ shouldRefreshServices: true }));
      }
      stopEditing();
      setError(null);
    } catch {
      const errorMessage = intl.formatMessage(messages.saveError);
      setError(errorMessage);
    }
  }, [walletId, editedNickname, dispatch, stopEditing, setError, intl, wallet.nickname]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSave();
      }

      if (event.key === 'Escape') {
        setEditedNickname('');
        setError(null);
        stopEditing();
      }
    },
    [handleSave, setError, stopEditing],
  );

  const handleCancel = useCallback(() => {
    setEditedNickname('');
    setError(null);
    stopEditing();
  }, [stopEditing, setError]);

  if (!wallet.walletId || !wallet.isWalletRegistered) {
    return null;
  }

  return (
    <div className={style['nickname-container']}>
      {!isEditing ? (
        <>
          <div className={style['nickname-text']}>{wallet.nickname}</div>
          <Button
            aria-label={intl.formatMessage(messages.editNickname)}
            className={style['edit-button']}
            onClick={handleEditClick}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FontAwesomeIcon className={style['pencil-icon']} icon={faPen} />
          </Button>
        </>
      ) : (
        <FlexColumn gap={Gap.Space8}>
          <FlexRow gap={Gap.Space8}>
            <Input
              className={style['nickname-input']}
              maxLength={MAX_NICKNAME_LENGTH}
              onChange={handleNicknameChange}
              onKeyDown={handleKeyDown}
              placeholder={intl.formatMessage(messages.nicknameInputPlaceholder)}
              value={editedNickname}
              version={version2}
            />
            <FlexRow>
              <Button
                aria-label={intl.formatMessage(messages.saveNickname)}
                className={style['action-button']}
                onClick={handleSave}
                theme={THEME_CONSTANTS.unstyled}
              >
                <FontAwesomeIcon className={style['save-icon']} icon={faCheck} />
              </Button>
              <Button
                aria-label={intl.formatMessage(messages.cancelEdit)}
                className={style['action-button']}
                onClick={handleCancel}
                theme={THEME_CONSTANTS.unstyled}
              >
                <FontAwesomeIcon className={style['cancel-icon']} icon={faXmark} />
              </Button>
            </FlexRow>
          </FlexRow>
          {error ? <Text className={style['error-message']} textType={TextType.Caption} value={error} /> : null}
        </FlexColumn>
      )}
    </div>
  );
};
